/**
 * Searchspring Active Facets Component
 *
 * Displays currently applied filters with remove functionality.
 * Updates automatically when filters change.
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} ActiveFilter
 * @property {string} field - Filter field name
 * @property {string} label - Filter display label
 * @property {string} value - Filter value
 * @property {string} displayValue - Value for display
 * @property {number} [count] - Result count (when available from API/facets)
 * @property {string} [swatchStyle] - Inline style for color swatch (when from color facet)
 */

/**
 * @typedef {object} Refs
 * @property {HTMLElement} container - Container for active filter tags
 * @property {HTMLElement} clearAllButton - Clear all filters button
 */

class SearchspringActiveFacetsComponent extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    // Listen for active facets update events
    document.addEventListener('ss:active-facets-update', this.#handleActiveFacetsUpdate, {
      signal,
    });

    // Listen for results loaded to update from URL
    document.addEventListener('ss:results-loaded', this.#handleResultsLoaded, { signal });

    // Optimistic update: show applied filters immediately when URL changes (before API returns)
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate, { signal });

    // Handle remove clicks (including Clear all inside this component)
    this.addEventListener('click', this.#handleClick, { signal });

    // Handle Clear all anywhere (e.g. drawer footer button)
    document.addEventListener('click', this.#handleDocumentClick, { signal });

    // Initial render from URL params
    this.#renderFromUrl();
  }

  /**
   * Handle document click for data-ss-clear-all (e.g. drawer Clear all button)
   * @param {Event} event
   */
  #handleDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-ss-clear-all]')) return;

    // Only one instance should handle each click. The drawer instance handles
    // clicks inside #filters-drawer; the non-drawer instance handles the rest.
    const clickInDrawer = target.closest('#filters-drawer') != null;
    const isDrawerInstance = this.hasAttribute('data-drawer-context');
    if (clickInDrawer !== isDrawerInstance) return;

    const drawer = clickInDrawer ? target.closest('#filters-drawer') : null;
    this.#clearAllFilters(drawer instanceof HTMLElement ? drawer : undefined);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Optimistic update: render applied filters from current URL when filter change is dispatched
   */
  #handleFilterUpdate = () => {
    this.#renderFromUrl('optimistic');
  };

  /**
   * Handle active facets update event from facets component
   * @param {CustomEvent} event
   */
  #handleActiveFacetsUpdate = (event) => {
    const { filterSummary } = event.detail;
    if (filterSummary) {
      this.#renderActiveFilters(filterSummary, 'api');
    }
  };

  /**
   * Handle results loaded event
   * @param {CustomEvent} event
   */
  #handleResultsLoaded = (event) => {
    const { facets } = event.detail;
    if (!facets) return;

    // Build filter summary from facets
    /** @type {ActiveFilter[]} */
    const activeFilters = [];
    /**
     * @typedef {object} FacetValue
     * @property {string} value
     * @property {string} label
     * @property {boolean} active
     * @property {number} [count]
     * @property {string} [color]
     */
    /**
     * @typedef {object} Facet
     * @property {string} field
     * @property {string} label
     * @property {string} [type]
     * @property {FacetValue[]} values
     */
    /** @type {Facet[]} */
    const facetsList = facets;
    facetsList.forEach((facet) => {
      const isColorFacet = facet.type === 'palette' || facet.values.some((v) => v.color != null);
      facet.values.forEach((value) => {
        if (value.active) {
          activeFilters.push({
            field: facet.field,
            label: facet.label,
            value: value.value,
            displayValue: value.label,
            count: value.count,
            swatchStyle: isColorFacet ? this.#getSwatchStyle(value) : undefined,
          });
        }
      });
    });

    this.#renderActiveFilters(activeFilters, 'api');
  };

  /**
   * Render active filters from URL parameters
   * @param {string} [source] - 'optimistic' when called from FilterUpdate, 'init' on first load
   */
  #renderFromUrl(source = 'init') {
    const urlParams = new URLSearchParams(window.location.search);
    /** @type {ActiveFilter[]} */
    const activeFilters = [];

    urlParams.forEach((value, key) => {
      if (key.startsWith('filter.')) {
        const field = key.replace('filter.', '');
        activeFilters.push({
          field,
          label: this.#formatLabel(field),
          value,
          displayValue: value,
        });
      }
    });

    this.#renderActiveFilters(activeFilters, source);
  }

  /**
   * Get swatch style for color facet value (matches facets component logic)
   * @param {{ color?: string, value: string }} value
   * @returns {string}
   */
  #getSwatchStyle(value) {
    const config = window.searchspringConfig || {};
    const colorSwatchPrefix = config.colorSwatchPrefix || '';
    if (value.color) {
      return `background-color: ${value.color};`;
    }
    if (colorSwatchPrefix) {
      const colorName = value.value.toLowerCase().replace(/\s+/g, '-');
      return `background-image: url('${colorSwatchPrefix}${colorName}.jpg');`;
    }
    const colorName = value.value.toLowerCase().replace(/\s+/g, '');
    return `background-color: ${colorName};`;
  }

  /**
   * Format field name as label
   * @param {string} field
   * @returns {string}
   */
  #formatLabel(field) {
    return field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase());
  }

  /**
   * Render active filter tags
   * @param {ActiveFilter[]} filters
   * @param {string} [source] - 'optimistic' | 'api' | 'init' for performance logging
   */
  #renderActiveFilters(filters, source = 'api') {
    const { container, clearAllButton, title } = this.refs;
    const containerEl = Array.isArray(container) ? container[0] : container;
    const clearAllEl = Array.isArray(clearAllButton) ? clearAllButton[0] : clearAllButton;
    const titleEl = Array.isArray(title) ? title[0] : title;
    if (!containerEl) return;

    // Update title with active filter count (e.g. "Applied filters (3)")
    const labelTemplate = this.dataset.appliedFiltersLabel;
    if (titleEl && labelTemplate) {
      titleEl.textContent = labelTemplate.replace('{count}', String(filters.length));
    }

    // Hide the whole block when no facets are applied
    this.toggleAttribute('hidden', filters.length === 0);

    // When in drawer, also show/hide the drawer footer Clear All button
    const inDrawer = this.hasAttribute('data-drawer-context');
    if (inDrawer) {
      const drawerClearAll = document.querySelector('#filters-drawer .ss-drawer-clear-all');
      if (drawerClearAll) {
        drawerClearAll.toggleAttribute('hidden', filters.length === 0);
      }
    }

    if (filters.length === 0) {
      containerEl.innerHTML = '';
      if (clearAllEl) {
        clearAllEl.toggleAttribute('hidden', true);
        containerEl.appendChild(clearAllEl);
      }
      return;
    }

    if (clearAllEl) {
      clearAllEl.toggleAttribute('hidden', false);
    }

    const pillsHtml = filters
      .map(
        (filter) => `
        <span class="ss-active-facets__tag" data-field="${filter.field}" data-value="${filter.value}">
          ${filter.swatchStyle ? `<span class="ss-active-facets__tag-swatch" style="${filter.swatchStyle}" aria-hidden="true"></span>` : ''}
          <span class="ss-active-facets__tag-value">${filter.displayValue}</span>
          <button
            type="button"
            class="ss-active-facets__tag-remove"
            aria-label="Remove ${filter.label}: ${filter.displayValue}"
            data-ss-remove-filter
            data-field="${filter.field}"
            data-value="${filter.value}"
          >
            <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </span>
      `,
      )
      .join('');

    containerEl.innerHTML = pillsHtml;
    if (clearAllEl) {
      const inDrawer = this.hasAttribute('data-drawer-context');
      if (inDrawer) {
        containerEl.appendChild(clearAllEl);
      } else {
        containerEl.prepend(clearAllEl);
      }
    }

    // Performance: log time from facet-filter-dispatched to applied filters visible
    const mark = performance.getEntriesByName('facet-filter-dispatched').pop();
    if (mark) {
      const duration = performance.now() - mark.startTime;
      if (typeof console !== 'undefined' && console.log) {
        console.log(`[SS Facets] Applied filters updated (${source}): ${duration.toFixed(0)} ms`);
      }
      performance.clearMarks('facet-filter-dispatched');
    }
  }

  /**
   * Handle click events
   * @param {Event} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const removeButton = target.closest('[data-ss-remove-filter]');
    if (removeButton) {
      event.stopPropagation();
      const field = removeButton.getAttribute('data-field');
      const value = removeButton.getAttribute('data-value');
      if (field != null && value != null) this.#removeFilter(field, value);
      return;
    }

    const clearAllButton = target.closest('[data-ss-clear-all]');
    if (clearAllButton) {
      event.stopPropagation();
      const drawer = clearAllButton.closest('#filters-drawer');
      this.#clearAllFilters(drawer instanceof HTMLElement ? drawer : undefined);
    }
  };

  /**
   * Remove a single filter
   * @param {string} field
   * @param {string} value
   */
  #removeFilter(field, value) {
    const url = new URL(window.location.href);
    const paramName = `filter.${field}`;

    // Get all values for this param
    const values = url.searchParams.getAll(paramName);

    // Remove the specific value
    url.searchParams.delete(paramName);

    // Re-add other values
    values.filter((v) => v !== value).forEach((v) => url.searchParams.append(paramName, v));

    url.searchParams.delete('page');

    history.pushState({}, '', url.toString());

    document.dispatchEvent(new CustomEvent(ThemeEvents.FilterUpdate));
  }

  /**
   * Clear all filters
   * @param {HTMLElement & { closeDialog?: () => Promise<void> } | undefined} [filtersDrawer] - When provided (e.g. click was inside #filters-drawer), close the drawer after clearing
   */
  #clearAllFilters(filtersDrawer) {
    const url = new URL(window.location.href);

    // Remove all filter params
    [...url.searchParams.keys()].forEach((key) => {
      if (key.startsWith('filter.')) {
        url.searchParams.delete(key);
      }
    });

    url.searchParams.delete('page');

    history.pushState({}, '', url.toString());

    document.dispatchEvent(
      new CustomEvent(ThemeEvents.FilterUpdate, {
        detail: { filters: [] },
      }),
    );

    if (filtersDrawer?.closeDialog) {
      filtersDrawer.closeDialog();
    }
  }
}

if (!customElements.get('searchspring-active-facets')) {
  customElements.define('searchspring-active-facets', SearchspringActiveFacetsComponent);
}

export { SearchspringActiveFacetsComponent };
